import { Component, computed, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { GeoAreaEditSessionService } from '@core/services/geo-area-edit-session/geo-area-edit-session.service';
import { ICONS } from '@core/services/icons/icons.service';
import {
  distinctVertexCount,
  outerRing,
  polygonAreaKm2,
  validatePolygon
} from '@core/utils/geo-polygon.utils';

/** Fewer corners than this is not a polygon, so Done cannot close the ring yet. */
const MIN_BOUNDARY_VERTICES = 3;

/**
 * The visible boundary of a drawing session: what the map is waiting for, what has been
 * drawn so far, and the two ways out. Without it the session has no end the user can
 * reach — Escape restarts the drawing and Enter only closes the ring.
 */
@Component({
  selector: 'app-geo-area-edit-panel',
  imports: [TranslatePipe],
  templateUrl: './geo-area-edit-panel.component.html'
})
export class GeoAreaEditPanelComponent {
  protected readonly ICONS = ICONS;
  protected readonly session = inject(GeoAreaEditSessionService);

  protected readonly problem = computed(() => validatePolygon(this.session.geometry()));

  protected readonly vertexCount = computed(() => {
    const geometry = this.session.geometry();
    return geometry ? distinctVertexCount(outerRing(geometry)) : 0;
  });

  protected readonly areaKm2 = computed(() => {
    const geometry = this.session.geometry();
    return geometry ? Math.round(polygonAreaKm2(geometry) * 10) / 10 : 0;
  });

  /**
   * Committing a boundary the backend would reject is worse than making the user fix it.
   *
   * A ring still being placed has no geometry yet, so it is judged on the vertices that
   * are down: Done closes the ring itself, and is offered as soon as there are enough for
   * one. Refusing below that is what stops Done from discarding a one- or two-point start.
   */
  protected readonly canCommit = computed(() => {
    const pending = this.session.pendingVertices();
    if (pending > 0) {
      return pending >= MIN_BOUNDARY_VERTICES;
    }

    const problem = this.problem();
    if (problem === 'empty') {
      return this.session.allowsEmpty();
    }
    return problem === null;
  });

  protected readonly pendingVertices = this.session.pendingVertices;

  protected readonly hasGeometry = computed(() => this.session.geometry() !== null);

  protected onClear(): void {
    this.session.clear();
  }

  protected onDiscard(): void {
    this.session.cancel();
  }

  protected onDone(): void {
    this.session.commit();
  }
}
